import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Paper, TextField } from '@mui/material';
import {
  ceilNextInt,
  ceilPercentageLabel,
  ceilRupeeLabel,
  ceilMixedQtyValue,
} from '../utils/ceilDisplay';
import './DailySummaryReport.css';

const DailySummaryReport = () => {
  const getYesterdayString = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  const [selectedDate, setSelectedDate] = useState(getYesterdayString());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://apir4prod.bsynapse.com/ifeccustomguard/dailyproductionreport', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ index: 'ifec', yesterday: selectedDate })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const rowsConfig = [
    { key: 'Total', label: 'Total', type: 'summary' },
    { key: 'CNC', label: 'CNC/VMC', type: 'summary' },
    { key: 'Manual', label: 'Manual', type: 'summary' },
    { key: 'Escorts', label: 'Escorts', type: 'brand' },
    { key: 'Carraro', label: 'Cararo', type: 'brand' },
    { key: 'ACE', label: 'Ace', type: 'brand' },
    { key: 'Kinetic', label: 'Kinetic', type: 'brand' },
    { key: 'Eicher', label: 'Eicher', type: 'brand' },
    { key: 'Customer', label: 'Customer', type: 'brand' },
    { key: 'Supplier', label: 'Supplier', type: 'brand' },
    { key: 'Target', label: 'Target', type: 'summary' }
  ];

  const resolveLookupKey = (key) => {
    if (!key || key === 'Customer' || key === 'Supplier' || key === 'Target') return null;
    let lookupKey = key;
    if (key === 'Carraro') lookupKey = 'Carraro';
    if (key === 'ACE') lookupKey = 'ACE';
    if (key === 'Total') lookupKey = 'total';
    if (key === 'CNC' || key === 'Manual') lookupKey = key.toLowerCase();
    return lookupKey;
  };

  const getPresentAbsentParts = (stats, field, rowKey) => {
    if (!stats) return null;
    const lookupKey = resolveLookupKey(rowKey);
    if (lookupKey === null) return null;
    const d = stats[field]?.[lookupKey];
    if (!d) return null;
    return {
      num: ceilNextInt(d.number),
      pct: ceilPercentageLabel(d.percentage),
    };
  };

  const getCellValue = (stats, category, key) => {
    if (!stats) return '';
    const lookupKey = resolveLookupKey(key);
    if (lookupKey === null) return '';

    const isBrand = !['Total', 'CNC', 'Manual'].includes(key);

    switch (category) {
      case 'wip': {
        const v = stats.wip?.[lookupKey] ?? stats.wip?.brands?.[lookupKey] ?? (isBrand || key === 'Total' ? 0 : '');
        if (v === '') return '';
        return ceilNextInt(v);
      }
      case 'ncr_count': {
        const v = stats.ncr_count?.[lookupKey] ?? (isBrand || key === 'Total' ? 0 : '');
        if (v === '') return '';
        return ceilNextInt(v);
      }
      case 'ncr_qty': {
        const v = stats.ncr_qty?.[lookupKey] ?? (isBrand || key === 'Total' ? 0 : '');
        if (v === '') return '';
        return ceilNextInt(v);
      }
      case 'total_present': {
        const d = stats.total_present?.[lookupKey];
        if (!d) return '';
        const n = ceilNextInt(d.number);
        const pct = ceilPercentageLabel(d.percentage);
        return `${n} (${pct})`;
      }
      case 'total_absent': {
        const d = stats.total_absent?.[lookupKey];
        if (!d) return '';
        const n = ceilNextInt(d.number);
        const pct = ceilPercentageLabel(d.percentage);
        return `${n} (${pct})`;
      }
      case 'ot_hrs': {
        const v = stats.ot_hrs?.[lookupKey];
        if (v === '' || v === null || v === undefined) return '';
        return ceilNextInt(v);
      }
      case 'ot_percentage': {
        const v = stats.ot_percentage?.[lookupKey];
        if (v === '' || v === null || v === undefined) return '';
        return ceilPercentageLabel(v);
      }
      case 'ot_value': {
        const v = stats.ot_value?.[lookupKey];
        if (v === '' || v === null || v === undefined) return '';
        return ceilRupeeLabel(v);
      }
      case 'unique_parts':
        if (Array.isArray(stats.unique_parts)) {
          return '';
        }
        {
          const v = stats.unique_parts?.[lookupKey] ?? (isBrand || key === 'Total' ? 0 : '');
          if (v === '') return '';
          return ceilNextInt(v);
        }
      case 'scrap_qty': {
        const v = stats.scrap_qty?.[lookupKey] ?? (isBrand || key === 'Total' ? 0 : '');
        if (v === '') return '';
        if (typeof v === 'number' && Number.isFinite(v)) return ceilNextInt(v);
        if (typeof v === 'string') return ceilMixedQtyValue(v);
        return v;
      }
      default:
        return '';
    }
  };

  const displayCell = (value) => {
    if (value === '' || value === null || value === undefined) return '\u2014';
    return value;
  };

  const MetricCell = ({ stats, category, row, className = '' }) => {
    if (
      stats &&
      (category === 'total_present' || category === 'total_absent')
    ) {
      const field = category === 'total_present' ? 'total_present' : 'total_absent';
      const parts = getPresentAbsentParts(stats, field, row.key);
      const empty = parts === null;
      return (
        <td
          className={['numeric-cell', 'numeric-cell--stack-pct', className, empty ? 'cell-empty' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {empty ? (
            '\u2014'
          ) : (
            <span className="metric-cell-stack">
              <span className="metric-value-primary">{parts.num}</span>
              <span className="metric-value-percent">({parts.pct})</span>
            </span>
          )}
        </td>
      );
    }

    if (stats && category === 'ot_percentage') {
      const raw = getCellValue(stats, category, row.key);
      const empty = raw === '' || raw === null || raw === undefined;
      return (
        <td
          className={['numeric-cell', 'numeric-cell--pct-col', className, empty ? 'cell-empty' : '']
            .filter(Boolean)
            .join(' ')}
        >
          {empty ? '\u2014' : <span className="metric-value-percent metric-value-percent--solo">{raw}</span>}
        </td>
      );
    }

    const raw = getCellValue(stats, category, row.key);
    const empty = raw === '' || raw === null || raw === undefined;
    return (
      <td className={['numeric-cell', className, empty ? 'cell-empty' : ''].filter(Boolean).join(' ')}>
        {displayCell(raw)}
      </td>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, color: 'red' }}>
        <Typography>Error loading data: {error}</Typography>
      </Box>
    );
  }

  const ys = data?.yesterdayStats;
  const ms = data?.monthlyStats;

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const selectedMonthName = selectedDate ? months[parseInt(selectedDate.split('-')[1], 10) - 1] : '';

  return (
    <Box
      sx={{
        height: 'calc(100vh - 64px)',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: '#fff',
      }}
    >
      <Paper 
        sx={{ 
          p: 1.5, 
          px: 3,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderRadius: 0,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          flexWrap: 'wrap',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)'
        }} 
        elevation={2}
      >
        <TextField
          label="Business Date"
          type="date"
          size="small"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{ minWidth: 200 }}
        />
      </Paper>
      <Box sx={{ p: 3 }}>
        <div className="daily-summary-container">
          <div className="table-responsive">
            <table className="summary-table">
              <thead>
                <tr className="thead-section-row">
                  <th rowSpan={2} className="corner-cell" aria-hidden="true" />
                  <th colSpan={10} className="section-title section-title--business border-right-section">
                    Business day <span className="section-title__meta">({selectedDate})</span>
                  </th>
                  <th colSpan={10} className="section-title section-title--month">
                    Total for the month <span className="section-title__meta">({selectedMonthName})</span>
                  </th>
                </tr>
                <tr className="thead-columns-row">
              <th className="col-head col-head--business">WIP</th>
              <th className="col-head col-head--business">NCR<br />(Count)</th>
              <th className="col-head col-head--business">NCR<br />(Qty)</th>
              <th className="col-head col-head--business">Total<br />Present –<br /><span className="col-head-percent-label">With %</span></th>
              <th className="col-head col-head--business">Total<br />Absent<br /><span className="col-head-percent-label">With %</span></th>
              <th className="col-head col-head--business">OT Hrs</th>
              <th className="col-head col-head--business"><span className="col-head-percent-label">% Of</span><br />OT</th>
              <th className="col-head col-head--business">OT value<br />(approx)</th>
              <th className="col-head col-head--business">Scrap<br />Qty &amp;<br />value</th>
              <th className="col-head col-head--business border-right-section">No. unique<br />parts<br />worked</th>

              <th className="col-head col-head--month">Total<br />WIP</th>
              <th className="col-head col-head--month">Total<br />NCR<br />(Count)</th>
              <th className="col-head col-head--month">Total<br />NCR<br />(Qty)</th>
              <th className="col-head col-head--month">Total<br />Present –<br /><span className="col-head-percent-label">With %</span></th>
              <th className="col-head col-head--month">Total<br />Absent<br /><span className="col-head-percent-label">with %</span></th>
              <th className="col-head col-head--month">Total<br />OT Hrs</th>
              <th className="col-head col-head--month">Total<br /><span className="col-head-percent-label">% Of</span><br />OT</th>
              <th className="col-head col-head--month">Total OT<br />value<br />(approx)</th>
              <th className="col-head col-head--month">Scrap<br />Qty &amp;<br />value</th>
              <th className="col-head col-head--month">Total no.<br />unique<br />parts<br />worked</th>
            </tr>
          </thead>
          <tbody>
            {rowsConfig.map((row, index) => {
              const isTotalRow = row.key === 'Total';
              const rowClass = isTotalRow ? 'data-row data-row--total' : 'data-row';

              return (
                <tr key={index} className={rowClass}>
                  <th scope="row" className="row-header">
                    {row.label === 'Total' ? <strong>{row.label}</strong> : row.label}
                  </th>

                  <MetricCell stats={ys} category="wip" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ncr_count" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ncr_qty" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="total_present" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="total_absent" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ot_hrs" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ot_percentage" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="ot_value" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="scrap_qty" row={row} className="cell-business-day" />
                  <MetricCell stats={ys} category="unique_parts" row={row} className="cell-business-day border-right-section" />

                  <MetricCell stats={ms} category="wip" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ncr_count" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ncr_qty" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="total_present" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="total_absent" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ot_hrs" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ot_percentage" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="ot_value" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="scrap_qty" row={row} className="cell-month" />
                  <MetricCell stats={ms} category="unique_parts" row={row} className="cell-month" />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
      </Box>
    </Box>
  );
};

export default DailySummaryReport;
